import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { TimePrecision, UserID } from '@playq/octopus-common';
import { PermTarget, GroupID, GroupBrief, Group } from '@playq/octopus2-admin';
import { UserFull, UserPublic } from '@playq/octopus2-auth';

import { services2 } from '/api/services2';
import { unwrapEither } from '/api/service-hooks/unwrapEither';
import { groupServiceBriefTags } from '/api/hooks/corp/groupsService';
import { usersServicePublicTags } from '/api/hooks/usersService';
import { appToolkit } from '/store';
import { toMs } from '/helpers';

export const usePermGetTargetsData = (targets: PermTarget[], fetchingEnabled = true) => {
  const queryClient = useQueryClient();

  const appID = useSelector(appToolkit.selectors.appID);

  const [enabled, setEnabled] = useState(false);

  const { users, usersSerialized, groups, groupsSerialized } = useMemo(() => {
    const usersArr = targets.filter((t) => t instanceof UserID);
    const groupsArr = targets.filter((target) => target instanceof GroupID);
    return {
      users: usersArr,
      usersSerialized: usersArr.map((u) => u.serialize()),
      groups: groupsArr,
      groupsSerialized: groupsArr.map((g) => g.serialize()),
    };
  }, [targets]);

  const usersQueryKey = usersServicePublicTags.concat(usersSerialized);
  const groupsQueryKey = groupServiceBriefTags.concat(groupsSerialized);

  const queryKey = usersQueryKey.concat(groupsQueryKey);

  const cachedData = queryClient.getQueryData<(GroupBrief | UserPublic)[]>(queryKey);

  const cachedTargetsData = queryClient
    .getQueriesData<(GroupBrief | UserPublic)[]>({
      predicate: (query) => {
        return (
          !groupServiceBriefTags.some((tag) => !query.queryKey.includes(tag)) ||
          !usersServicePublicTags.some((tag) => !query.queryKey.includes(tag))
        );
      },
    })
    .map(([, data]) => data ?? [])
    .flat();

  useEffect(() => {
    if (enabled) {
      return;
    }
    if (cachedData === undefined && cachedTargetsData.length === 0) {
      setEnabled(true);
    }
  }, [cachedData, cachedTargetsData, enabled]);

  useEffect(() => {
    setEnabled(true);
  }, [appID]);

  const addUser = (user: UserFull) => {
    const userPublic = new UserPublic();
    userPublic.id = user.id;
    userPublic.name = user.name;
    userPublic.mfa = user.mfa;
    userPublic.email = user.email;

    const userId = user.id.serialize();
    queryClient.setQueryData(usersServicePublicTags.concat(userId), [userPublic]);
    queryClient.setQueryData<(GroupBrief | UserPublic)[]>(usersQueryKey.concat(userId, groupsQueryKey), (prevData) => [
      ...(prevData ?? []),
      userPublic,
    ]);
  };

  const addGroup = (group: Group) => {
    const groupBrief = new GroupBrief();
    groupBrief.id = group.id;
    groupBrief.name = group.name;
    groupBrief.description = group.description;

    const groupId = group.id.serialize();
    queryClient.setQueryData(groupServiceBriefTags.concat(groupId), [groupBrief]);
    queryClient.setQueryData<(GroupBrief | UserPublic)[]>(queryKey.concat(groupId), (prevData) => [
      ...(prevData ?? []),
      groupBrief,
    ]);
  };

  const addEntity = (entity: UserFull | Group) => {
    if (entity instanceof UserFull) {
      addUser(entity);
    } else if (entity instanceof Group) {
      addGroup(entity);
    }
  };

  const response = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await Promise.all([
        unwrapEither(services2.adminGroupsService.briefFor(groups)),
        unwrapEither(services2.usersService.retrievePublicFor(users)),
      ]);

      setEnabled(false);

      const flattenData = res.flat();
      return flattenData;
    },
    enabled: enabled && fetchingEnabled && targets.length > 0,
    staleTime: toMs(TimePrecision.Minute, 10),
  });

  return {
    ...response,
    data: cachedTargetsData,
    addEntity,
  };
};
